import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle, RefreshCw, Info } from 'lucide-react';

export default function SoSSettingsPanel() {
  const [masterInfo, setMasterInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadMasterInfo = async () => {
    try {
      const res = await api.get(`${API}/stock-ordering/master/info`, { params: { branch_id: 'C30' } });
      setMasterInfo(res.data);
    } catch {
      setMasterInfo({ has_master: false });
    }
  };

  useEffect(() => { loadMasterInfo(); }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Please upload a .xlsx file');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('branch_id', 'C30');
    try {
      const res = await api.post(`${API}/stock-ordering/master/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Master file uploaded — ${res.data.row_count.toLocaleString()} products loaded`);
      await loadMasterInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-orange-500" />
            Stock Order Suggestion (SoS) Settings
          </CardTitle>
          <CardDescription>
            Manage the master reference file and settings for the Stock Ordering system.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Master File Section */}
          <div className="border rounded-lg p-5 space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Master SOS File
            </h3>
            <p className="text-sm text-gray-500">
              This is the master reference spreadsheet (e.g. <code className="bg-gray-100 px-1 rounded text-xs">C30_Stock_Order_Suggestion_B29_Integrated.xlsx</code>).
              Upload it here whenever the master copy needs to be updated. All product data, supplier mappings, and suggested order quantities come from this file.
            </p>

            {masterInfo?.has_master ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Master file loaded
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><span className="text-gray-400">File:</span> {masterInfo.filename}</div>
                  <div><span className="text-gray-400">Products:</span> {masterInfo.row_count?.toLocaleString()}</div>
                  <div><span className="text-gray-400">Uploaded:</span> {masterInfo.upload_date ? new Date(masterInfo.upload_date).toLocaleString('en-GB') : '—'}</div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-700">No master file uploaded yet. Upload the SOS master xlsx to enable the Stock Ordering system.</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="sos-upload-master-btn"
                className="flex items-center gap-2"
              >
                {uploading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {uploading ? 'Uploading...' : masterInfo?.has_master ? 'Replace Master File' : 'Upload Master File'}
              </Button>
              <span className="text-xs text-gray-400">.xlsx files only</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleFileUpload}
              data-testid="sos-master-file-input"
            />
          </div>

          {/* Future Settings placeholder */}
          <div className="border rounded-lg p-5 bg-gray-50">
            <h3 className="font-semibold text-gray-500 text-sm uppercase tracking-wide mb-2">Future Settings</h3>
            <p className="text-sm text-gray-400">
              Additional SoS configuration options (branch-specific settings, suggestion thresholds, supplier exclusions) will appear here.
            </p>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}
